import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Translate as TranslateIcon } from '@mui/icons-material';
import { tourService, globalService } from '../../services/firebase';
import { Tour, TranslatableField } from '../../types';
import MultipleImageUploadField from '../../components/Common/MultipleImageUploadField';
import { useTranslation } from 'react-i18next';
import { getTranslatedData, setTranslatedData } from '../../utils/getTranslatedData';

// Helper for translatable fields that are simple strings
const getTranslatableString = (field: TranslatableField | string | undefined, lang: string): string => {
  if (!field) return '';
  if (typeof field === 'string') {
    // If field is a string, it's only available for 'tr' or should be empty for other langs
    return lang === 'tr' ? field : '';
  }
  return field[lang] || '';
};

const ManageToursPage: React.FC = () => {
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentTour, setCurrentTour] = useState<Partial<Tour> | null>(null);
  const [selectedLang, setSelectedLang] = useState('tr');
  const [isSaving, setIsSaving] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { t, i18n } = useTranslation();

  const fetchTours = useCallback(async () => {
    setLoading(true);
    try {
      const toursData = await tourService.getTours();
      setTours(toursData);
    } catch (err) {
      setError(t('manageTours.errors.fetchError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTours();
  }, [fetchTours]);

  useEffect(() => {
    // Automatically calculate duration when start/end dates change
    if (currentTour?.startDate && currentTour?.endDate) {
      const start = new Date(currentTour.startDate);
      const end = new Date(currentTour.endDate);
      if (start <= end) {
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        if (currentTour.duration !== diffDays) {
          setCurrentTour(prev => prev ? { ...prev, duration: diffDays } : null);
        }
      }
    }
  }, [currentTour?.startDate, currentTour?.endDate, currentTour?.duration]);


  const handleOpenDialog = (tour: Tour | null = null) => {
    if (tour) {
      // Editing an existing tour
      setCurrentTour(JSON.parse(JSON.stringify(tour))); // Deep copy to avoid direct state mutation
    } else {
      // Creating a new tour
      setCurrentTour({
        name: { tr: '', en: '', ar: '' },
        destination: { tr: '', en: '', ar: '' },
        category: { tr: '', en: '', ar: '' },
        continent: { tr: '', en: '', ar: '' },
        description: { tr: '', en: '', ar: '' },
        shortDescription: '', // Not translatable
        itinerary: { tr: '', en: '', ar: '' },
        highlights: { tr: '', en: '', ar: '' },
        included: { tr: '', en: '', ar: '' },
        notIncluded: { tr: '', en: '', ar: '' },
        preparation: { tr: '', en: '', ar: '' },
        souvenirs: { tr: '', en: '', ar: '' },
        price: 0,
        duration: 1,
        minAge: 18,
        maxCapacity: 20,
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        images: [],
        isActive: true,
        isFeatured: false,
      });
    }
    setSelectedLang('tr');
    setError(null);
    setSuccess(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentTour(null);
  };

  const handleInputChange = (field: keyof Tour, value: any) => {
    setCurrentTour(prev => {
        if (!prev) return null;
        return { ...prev, [field]: value };
    });
  };

  const handleTranslatableInputChange = (field: keyof Tour, value: string) => {
    setCurrentTour(prev => {
        if (!prev) return null;
        const newTour = { ...prev };
        const existingValue = (newTour as any)[field];
        let fieldAsObject: TranslatableField;

        if (typeof existingValue === 'object' && existingValue !== null) {
            fieldAsObject = { ...existingValue, [selectedLang]: value };
        } else {
            const trValue = typeof existingValue === 'string' ? existingValue : '';
            fieldAsObject = { tr: trValue, en: '', ar: '' };
            fieldAsObject[selectedLang] = value;
        }
        (newTour as any)[field] = fieldAsObject;
        return newTour;
    });
  };

  const handleSaveTour = async () => {
    if (!currentTour) return;

    setIsSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const tourToSave: Partial<Tour> = { ...currentTour };
      
      if (currentTour.id) {
        await tourService.updateTour(currentTour.id, tourToSave as Omit<Tour, 'id'>);
        setSuccess(t('manageTours.success.update'));
      } else {
        await tourService.createTour(tourToSave as Omit<Tour, 'id' | 'createdAt' | 'updatedAt'>);
        setSuccess(t('manageTours.success.create'));
      }
      fetchTours();
      handleCloseDialog();
    } catch (err: any) {
      setError(t('manageTours.errors.saveError', { message: err.message }));
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTour = async (id: string) => {
    if (window.confirm(t('manageTours.confirmDelete'))) {
      try {
        await tourService.deleteTour(id);
        setSuccess(t('manageTours.success.delete'));
        fetchTours();
      } catch (err: any) {
        setError(t('manageTours.errors.deleteError', { message: err.message }));
        console.error(err);
      }
    }
  };

  const handleTranslateAll = async () => {
    if (!currentTour) return;

    setIsTranslating(true);
    setError(null);
    
    const translatableFields: (keyof Tour)[] = [
        'name', 'destination', 'category', 'continent', 
        'description', 'itinerary', 
        'highlights', 'included', 'notIncluded', 
        'preparation', 'souvenirs'
    ];

    try {
        let translatedTour = { ...currentTour };
        for (const field of translatableFields) {
            const sourceText = getTranslatableString(translatedTour[field] as TranslatableField | string | undefined, 'tr');
            if (sourceText) {
                const translations = await globalService.translateTexts({
                    text: sourceText,
                    sourceLang: 'tr',
                    targetLangs: ['en', 'ar'],
                });
                (translatedTour as any)[field] = {
                  tr: sourceText,
                  en: (translations as any).en,
                  ar: (translations as any).ar,
                };
            }
        }
        setCurrentTour(translatedTour);
        setSuccess(t('manageTours.success.translate'));
    } catch (err: any) {
        setError(t('manageTours.errors.translateError', { message: err.message }));
        console.error(err);
    } finally {
        setIsTranslating(false);
    }
  };


  const columns: GridColDef<Tour>[] = [
    { field: 'name', headerName: t('manageTours.grid.name'), width: 250,
      valueGetter: (value, row) => getTranslatedData(row.name, i18n.language) || t('unnamed')
    },
    { field: 'destination', headerName: t('manageTours.grid.destination'), width: 150,
      valueGetter: (value, row) => getTranslatedData(row.destination, i18n.language)
    },
    { field: 'category', headerName: t('manageTours.grid.category'), width: 130,
      valueGetter: (value, row) => getTranslatedData(row.category, i18n.language)
    },
    { field: 'price', headerName: t('manageTours.grid.price'), width: 100, type: 'number',
      valueFormatter: (value: number | null) => value != null ? `${value.toLocaleString('tr-TR')} ₺` : ''
    },
    { field: 'duration', headerName: t('manageTours.grid.duration'), width: 100, type: 'number',
      valueFormatter: (value: number | null) => value != null ? `${value} ${t('days', { count: value })}` : ''
    },
    { field: 'isActive', headerName: t('manageTours.grid.status'), width: 100, type: 'boolean' },
    { field: 'isFeatured', headerName: t('manageTours.grid.featured'), width: 100, type: 'boolean' },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('manageTours.grid.actions'),
      width: 120,
      getActions: (params: GridRowParams<Tour>) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label={t('edit')}
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon sx={{ color: 'error.main' }} />}
          label={t('delete')}
          onClick={() => handleDeleteTour(params.id as string)}
        />,
      ],
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">{t('manageTours.title')}</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            {t('manageTours.addTour')}
          </Button>
        </Box>

        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

        <Box sx={{ height: 650, width: '100%' }}>
          <DataGrid
            rows={tours}
            columns={columns}
            loading={loading}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[10, 25, 50]}
            getRowId={(row) => row.id}
          />
        </Box>
      </Paper>
      
      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle>{currentTour?.id ? t('manageTours.form.editTitle') : t('manageTours.form.createTitle')}</DialogTitle>
        <DialogContent>
            {currentTour && (
                <Box>
                    <Tabs value={selectedLang} onChange={(_, newValue) => setSelectedLang(newValue)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                        <Tab label="Türkçe" value="tr" />
                        <Tab label="English" value="en" />
                        <Tab label="العربية" value="ar" />
                    </Tabs>
                    
                    <Box 
                      component="form"
                      sx={{ 
                        display: 'grid',
                        gridTemplateColumns: { sm: 'repeat(12, 1fr)' },
                        gap: 2,
                        mt: 1
                      }}
                    >
                        {/* Basic Info */}
                        <TextField sx={{ gridColumn: { sm: 'span 8' } }} label={t('manageTours.form.name')} fullWidth value={getTranslatableString(currentTour.name, selectedLang)} onChange={(e) => handleTranslatableInputChange('name', e.target.value)} />
                        <TextField sx={{ gridColumn: { sm: 'span 4' } }} label={t('manageTours.form.price')} type="number" fullWidth value={currentTour.price || ''} onChange={(e) => handleInputChange('price', Number(e.target.value))} InputProps={{ endAdornment: '₺' }} />
                        
                        <TextField sx={{ gridColumn: { sm: 'span 6' } }} label={t('manageTours.form.destination')} fullWidth value={getTranslatableString(currentTour.destination, selectedLang)} onChange={(e) => handleTranslatableInputChange('destination', e.target.value)} />
                        <TextField sx={{ gridColumn: { sm: 'span 6' } }} label={t('manageTours.form.category')} fullWidth value={getTranslatableString(currentTour.category, selectedLang)} onChange={(e) => handleTranslatableInputChange('category', e.target.value)} />
                        <TextField sx={{ gridColumn: { sm: 'span 6' } }} label={t('manageTours.form.continent')} fullWidth value={getTranslatableString(currentTour.continent, selectedLang)} onChange={(e) => handleTranslatableInputChange('continent', e.target.value)} />
                        
                        {/* Dates & Numbers */}
                         <TextField sx={{ gridColumn: { sm: 'span 3' } }} label={t('manageTours.form.startDate')} type="date" fullWidth value={currentTour.startDate || ''} onChange={(e) => handleInputChange('startDate', e.target.value)} InputLabelProps={{ shrink: true }}/>
                         <TextField sx={{ gridColumn: { sm: 'span 3' } }} label={t('manageTours.form.endDate')} type="date" fullWidth value={currentTour.endDate || ''} onChange={(e) => handleInputChange('endDate', e.target.value)} InputLabelProps={{ shrink: true }}/>
                        <TextField sx={{ gridColumn: { sm: 'span 2' } }} label={t('manageTours.form.duration')} type="number" fullWidth value={currentTour.duration || ''} InputProps={{ readOnly: true }} helperText={t('manageTours.form.durationHelper')} />
                        <TextField sx={{ gridColumn: { sm: 'span 2' } }} label={t('manageTours.form.capacity')} type="number" fullWidth value={currentTour.maxCapacity || ''} onChange={(e) => handleInputChange('maxCapacity', Number(e.target.value))} />
                        <TextField sx={{ gridColumn: { sm: 'span 2' } }} label={t('manageTours.form.minAge')} type="number" fullWidth value={currentTour.minAge || ''} onChange={(e) => handleInputChange('minAge', Number(e.target.value))} />

                        {/* Descriptions */}
                        <TextField sx={{ gridColumn: 'span 12' }} label={t('manageTours.form.shortDescription')} multiline rows={2} fullWidth value={currentTour.shortDescription || ''} onChange={(e) => handleInputChange('shortDescription', e.target.value)} />
                        <TextField sx={{ gridColumn: 'span 12' }} label={t('manageTours.form.description')} multiline rows={5} fullWidth value={getTranslatableString(currentTour.description, selectedLang)} onChange={(e) => handleTranslatableInputChange('description', e.target.value)} />
                        <TextField sx={{ gridColumn: 'span 12' }} label={t('manageTours.form.itinerary')} multiline rows={5} fullWidth value={getTranslatableString(currentTour.itinerary, selectedLang)} onChange={(e) => handleTranslatableInputChange('itinerary', e.target.value)} helperText={t('manageTours.form.listHelper')}/>
                        <TextField sx={{ gridColumn: { sm: 'span 6' } }} label={t('manageTours.form.highlights')} multiline rows={4} fullWidth value={getTranslatableString(currentTour.highlights, selectedLang)} onChange={(e) => handleTranslatableInputChange('highlights', e.target.value)} helperText={t('manageTours.form.listHelper')}/>
                        <TextField sx={{ gridColumn: { sm: 'span 6' } }} label={t('manageTours.form.included')} multiline rows={4} fullWidth value={getTranslatableString(currentTour.included, selectedLang)} onChange={(e) => handleTranslatableInputChange('included', e.target.value)} helperText={t('manageTours.form.listHelper')}/>
                        <TextField sx={{ gridColumn: { sm: 'span 6' } }} label={t('manageTours.form.notIncluded')} multiline rows={4} fullWidth value={getTranslatableString(currentTour.notIncluded, selectedLang)} onChange={(e) => handleTranslatableInputChange('notIncluded', e.target.value)} helperText={t('manageTours.form.listHelper')}/>
                        <TextField sx={{ gridColumn: { sm: 'span 6' } }} label={t('manageTours.form.preparation')} multiline rows={4} fullWidth value={getTranslatableString(currentTour.preparation, selectedLang)} onChange={(e) => handleTranslatableInputChange('preparation', e.target.value)} helperText={t('manageTours.form.listHelper')}/>
                        <TextField sx={{ gridColumn: 'span 12' }} label={t('manageTours.form.souvenirs')} multiline rows={2} fullWidth value={getTranslatableString(currentTour.souvenirs, selectedLang)} onChange={(e) => handleTranslatableInputChange('souvenirs', e.target.value)} helperText={t('manageTours.form.listHelper')}/>

                        {/* Image Upload */}
                        <Box sx={{ gridColumn: 'span 12' }}>
                          <MultipleImageUploadField
                            label={t('manageTours.form.images')}
                            storagePath="tours"
                            value={currentTour.images || []}
                            onChange={(urls: string[]) => handleInputChange('images', urls)}
                          />
                        </Box>

                        {/* Switches */}
                        <Box sx={{ gridColumn: { sm: 'span 6' } }}>
                           <FormControlLabel control={<Switch checked={currentTour.isActive || false} onChange={(e) => handleInputChange('isActive', e.target.checked)}/>} label={t('manageTours.form.active')}/>
                        </Box>
                         <Box sx={{ gridColumn: { sm: 'span 6' } }}>
                           <FormControlLabel control={<Switch checked={currentTour.isFeatured || false} onChange={(e) => handleInputChange('isFeatured', e.target.checked)}/>} label={t('manageTours.form.featured')}/>
                        </Box>
                    </Box>
                </Box>
            )}
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          <Tooltip title={t('manageTours.form.translateTooltip')}>
            <IconButton onClick={handleTranslateAll} disabled={isTranslating || selectedLang !== 'tr'}>
              {isTranslating ? <CircularProgress size={24} /> : <TranslateIcon />}
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button onClick={handleCloseDialog}>{t('cancel')}</Button>
          <Button onClick={handleSaveTour} variant="contained" disabled={isSaving}>
            {isSaving ? t('saving') : t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageToursPage;